/**
 * AdminBrands — organizer's list of brands they own (`/admin/brands`).
 * Lists existing brands + lets them spin up a new one.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Plus, Calendar, Users, ExternalLink, ArrowLeft, Globe, EyeOff,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBrands() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', tagline: '', city: '', kind: 'series' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/brands/me`, { headers });
      setBrands(res.data?.brands || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/brands`, {
        name: form.name.trim(),
        tagline: form.tagline.trim() || undefined,
        city: form.city.trim() || undefined,
        kind: form.kind || 'series',
      }, { headers });
      toast.success(`${(form.kind || 'series')[0].toUpperCase()}${(form.kind || 'series').slice(1)} created!`);
      setForm({ name: '', tagline: '', city: '', kind: 'series' });
      setShowForm(false);
      navigate(`/admin/brands/${res.data.brand.brand_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create brand');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="admin-brands">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/events')} data-testid="back-to-events">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">Series &amp; Clubs</h1>
            <p className="text-sm text-muted-foreground">
              Group your recurring events under a series, club or program. Sell memberships and passports per group.
            </p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="new-brand-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New series / club
          </Button>
        </div>

        {showForm && (
          <Card className="bg-card border-border mb-5">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-semibold text-foreground">Create a new brand</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    placeholder="e.g. Denver Wine & Cheese Experience"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="brand-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">City <span className="opacity-60">(optional)</span></Label>
                  <Input
                    placeholder="denver"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    data-testid="brand-city-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Tagline</Label>
                <Input
                  placeholder="One-line pitch for the series"
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  data-testid="brand-tagline-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setShowForm(false)} data-testid="brand-create-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="brand-create-save"
                >
                  {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
          </div>
        ) : brands.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-10 text-center">
              <h3 className="text-lg font-semibold text-foreground">No series or clubs yet</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
              Create a series, club or program to group events under a single identity and sell memberships.
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus className="h-4 w-4 mr-2" /> Create your first series
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-brands-grid">
            {brands.map((b) => (
              <Card key={b.brand_id} className="bg-card border-border hover:border-indigo-700/60 transition-colors" data-testid={`brand-row-${b.brand_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt="" className="h-12 w-12 rounded-md border border-border object-cover shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-md bg-indigo-500/15 flex items-center justify-center shrink-0">
                        <Calendar className="h-5 w-5 text-indigo-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-foreground truncate">{b.name}</h3>
                        {b.is_published ? (
                          <Badge variant="outline" className="text-emerald-300 border-emerald-700/40 bg-emerald-500/10 text-[10px]">
                            <Globe className="h-2.5 w-2.5 mr-1" /> Live
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground border-border bg-muted/30 text-[10px]">
                            <EyeOff className="h-2.5 w-2.5 mr-1" /> Draft
                          </Badge>
                        )}
                      </div>
                      {b.tagline && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{b.tagline}</p>}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{b.events_count} events</span>
                        <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{b.members_count} members</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/brands/${b.brand_id}`)}
                      data-testid={`brand-manage-${b.brand_id}`}
                    >
                      Manage
                    </Button>
                    {b.is_published && (
                      <Link to={`/series/${b.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="ghost" data-testid={`brand-view-public-${b.brand_id}`}>
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View public page
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
